"use client";
import { API_BASE_URL } from "@kupikupi/brand";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { CaptchaTokenProvider } from "../captcha-retry";
import { sendEmailCaptcha } from "../endpoints/user";
import type { SendEmailCaptchaRequest } from "../types/user";

interface CaptchaProviderOpts {
  captchaTokenProvider?: CaptchaTokenProvider | undefined;
}

/**
 * Wraps `POST /user/email/captcha` with a 60s send-again cooldown.
 * Backend `UserController:275-279` enforces the same 60s lock server-side
 * (via `vcode:<type>:<email>_expire` Redis key), so the UI countdown here
 * is purely an affordance — server is authoritative.
 */
export function useSendEmailCode(opts: CaptchaProviderOpts = {}) {
  const [cooldown, setCooldown] = useState(0);
  useEffect(() => {
    if (cooldown <= 0) return;
    const handle = setTimeout(() => setCooldown((c: number) => c - 1), 1000);
    return () => clearTimeout(handle);
  }, [cooldown]);

  const mutation = useMutation({
    mutationFn: async (body: SendEmailCaptchaRequest) => {
      const captchaToken = await opts.captchaTokenProvider?.();
      return sendEmailCaptcha(
        { baseUrl: API_BASE_URL },
        captchaToken === undefined ? body : { ...body, captchaToken },
      );
    },
    onSuccess: () => setCooldown(60),
  });

  return {
    ...mutation,
    cooldown,
    canSend: cooldown === 0 && !mutation.isPending,
  };
}
